import React, { useState, useEffect } from 'react';
import { Typography, DatePicker, Button, Table, Space, Tag, message, Modal, Form, Input, InputNumber, Grid, List, Card } from 'antd';
import { ExclamationCircleOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { getDangKyOnlines, toggleDangKyStatus, updateDangKyOnline, deleteDangKyOnline, createDangKyOnline, DangKyOnlinePayload } from '../services/dangKyOnlineService';
import dayjs from 'dayjs';

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { useBreakpoint } = Grid;

// Define the type for our data records
interface DangKyOnlineRecord {
    stt: number;
    tenBn: string;
    namSinh: number;
    sdt: string;
    diaChi: string;
    lyDoDenKham: string;
    ngayDangKyKham: string;
    ngayTao: string;
    ghiChu?: string;
    trangThai: number;
}

// Helper to check permissions
const hasPermission = (permission: string) => {
    const storedPermissions = localStorage.getItem('permissions');
    if (!storedPermissions) return false;
    try {
        const permissionsList: string[] = JSON.parse(storedPermissions);
        return permissionsList.includes(permission);
    } catch (e) {
        console.error("Failed to parse permissions from localStorage", e);
        return false;
    }
};


const DangKyOnlineManagement: React.FC = () => {
    const [data, setData] = useState<DangKyOnlineRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([dayjs().startOf('month'), dayjs().endOf('month')]);
    const [nameSearch, setNameSearch] = useState('');
    
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [editingRecord, setEditingRecord] = useState<DangKyOnlineRecord | null>(null);
    const [editForm] = Form.useForm();

    const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
    const [createForm] = Form.useForm();

    const screens = useBreakpoint();


    const fetchData = async (range: [dayjs.Dayjs, dayjs.Dayjs], name?: string) => {
        setLoading(true);
        try {
            const [fromDate, toDate] = range;
            const response = await getDangKyOnlines(fromDate.toISOString(), toDate.toISOString(), name);
            setData(response.data);
        } catch (error) {
            console.error("Failed to fetch data:", error);
            message.error('Không thể tải dữ liệu đăng ký.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(dateRange, nameSearch);
    }, []);

    const handleFilter = () => {
        fetchData(dateRange, nameSearch);
    };

    const handleToggleStatus = async (id: number) => {
        try {
            const response = await toggleDangKyStatus(id);
            setData(prevData =>
                prevData.map(item =>
                    item.stt === id ? { ...item, trangThai: response.data.trangThai } : item
                )
            );
            message.success('Cập nhật trạng thái thành công!');
        } catch (error) {
            console.error("Failed to toggle status:", error);
            message.error('Cập nhật trạng thái thất bại.');
        }
    };

    const showEditModal = (record: DangKyOnlineRecord) => {
        setEditingRecord(record);
        editForm.setFieldsValue({
            ...record,
            ngayDangKyKham: dayjs(record.ngayDangKyKham),
        });
        setIsEditModalVisible(true);
    };

    const handleEditCancel = () => {
        setIsEditModalVisible(false);
        setEditingRecord(null);
    };

    const handleEditSubmit = async (values: any) => {
        if (!editingRecord) return;
        try {
            const payload: DangKyOnlinePayload = {
                ...values,
                namSinh: parseInt(values.namSinh, 10),
                ngayDangKyKham: values.ngayDangKyKham.toISOString(),
            };
            await updateDangKyOnline(editingRecord.stt, payload);
            message.success('Cập nhật thông tin thành công!');
            handleEditCancel();
            fetchData(dateRange, nameSearch); // Refresh data
        } catch (error) {
            console.error('Failed to update record:', error);
            message.error('Cập nhật thông tin thất bại.');
        }
    };

    const handleDelete = (id: number) => {
        Modal.confirm({
            title: 'Bạn có chắc chắn muốn xoá?',
            icon: <ExclamationCircleOutlined />,
            content: 'Hành động này không thể được hoàn tác.',
            okText: 'Xoá',
            okType: 'danger',
            cancelText: 'Huỷ',
            onOk: async () => {
                try {
                    await deleteDangKyOnline(id);
                    setData(prevData => prevData.filter(item => item.stt !== id));
                    message.success('Đã xoá đăng ký thành công!');
                } catch (error) {
                    console.error('Failed to delete record:', error);
                    message.error('Xoá đăng ký thất bại.');
                }
            },
        });
    };

    // Create New Registration Handlers
    const showCreateModal = () => {
        createForm.resetFields();
        setIsCreateModalVisible(true);
    };

    const handleCreateCancel = () => {
        setIsCreateModalVisible(false);
    };


    const handleCreateSubmit = async (values: any) => {
        try {
            const payload: DangKyOnlinePayload = {
                ...values,
                namSinh: parseInt(values.namSinh, 10),
                ngayDangKyKham: values.ngayDangKyKham.toISOString(),
            };
            await createDangKyOnline(payload);
            message.success('Tạo đăng ký mới thành công!');
            handleCreateCancel();
            fetchData(dateRange, nameSearch); // Refresh data
        } catch (error) {
            console.error('Failed to create record:', error);
            message.error('Tạo đăng ký mới thất bại.');
        }
    };
    
    // Disabled date logic for DatePicker
    const disabledDate = (current: dayjs.Dayjs) => {
        const today = dayjs().startOf('day');
        const sevenDaysLater = dayjs().add(7, 'day').endOf('day');
        return current && (current.isBefore(today) || current.isAfter(sevenDaysLater));
    };


    const columns = [
        {
            title: 'STT',
            dataIndex: 'stt',
            key: 'stt',
            width: 60,
        },
        {
            title: 'Tên Bệnh Nhân',
            dataIndex: 'tenBn',
            key: 'tenBn',
        },
        {
            title: 'Năm Sinh',
            dataIndex: 'namSinh',
            key: 'namSinh',
        },
        {
            title: 'Số Điện Thoại',
            dataIndex: 'sdt',
            key: 'sdt',
        },
        {
            title: 'Địa chỉ',
            dataIndex: 'diaChi',
            key: 'diaChi',
        },
        {
            title: 'Lý Do Đến Khám',
            dataIndex: 'lyDoDenKham',
            key: 'lyDoDenKham',
        },
        {
            title: 'Ghi Chú',
            dataIndex: 'ghiChu',
            key: 'ghiChu',
        },
        {
            title: 'Ngày Tạo',
            dataIndex: 'ngayTao',
            key: 'ngayTao',
            render: (text: string) => dayjs(text).format('HH:mm DD/MM/YYYY'),
        },
        {
            title: 'Ngày Đăng Ký Khám',
            dataIndex: 'ngayDangKyKham',
            key: 'ngayDangKyKham',
            render: (text: string) => dayjs(text).format('HH:mm DD/MM/YYYY'),
        },
        {
            title: 'Trạng Thái',
            dataIndex: 'trangThai',
            key: 'trangThai',
            render: (status: number) => (
                <Tag color={status === 1 ? 'green' : 'red'}>
                    {status === 1 ? 'Đã đăng ký' : 'Chưa đăng ký'}
                </Tag>
            ),
        },
        {
            title: 'Hành Động',
            key: 'action',
            render: (_: any, record: DangKyOnlineRecord) => (
                <Space size="middle">
                    <Button 
                        type={record.trangThai === 1 ? "default" : "primary"}
                        onClick={() => handleToggleStatus(record.stt)}
                    >
                        {record.trangThai === 1 ? 'Hủy ĐK' : 'Đăng ký'}
                    </Button>
                    {hasPermission('DangKyOnlineDel') && (
                        <>
                            <Button onClick={() => showEditModal(record)}>Sửa</Button>
                            <Button danger onClick={() => handleDelete(record.stt)}>Xoá</Button>
                        </>
                    )}
                </Space>
            ),
        },
    ];

    const renderDesktopView = () => (
        <Table columns={columns} dataSource={data} loading={loading} rowKey="stt" scroll={{ x: 'max-content' }} />
    );

    const renderMobileView = () => (
        <List
            loading={loading}
            dataSource={data}
            renderItem={(item: DangKyOnlineRecord) => (
                <List.Item>
                    <Card 
                        title={`${item.stt} - ${item.tenBn}`}
                        style={{ width: '100%' }}
                        actions={[
                             <Button 
                                type={item.trangThai === 1 ? "default" : "primary"}
                                onClick={() => handleToggleStatus(item.stt)}
                                size="small"
                            >
                                {item.trangThai === 1 ? 'Hủy ĐK' : 'Đăng ký'}
                            </Button>,
                            ...(hasPermission('DangKyOnlineDel') ? [
                                <Button size="small" onClick={() => showEditModal(item)}>Sửa</Button>,
                                <Button size="small" danger onClick={() => handleDelete(item.stt)}>Xoá</Button>
                            ] : [])
                        ]}
                    >
                        <p><strong>Năm sinh:</strong> {item.namSinh}</p>
                        <p><strong>Số điện thoại:</strong> {item.sdt}</p>
                        <p><strong>Địa chỉ:</strong> {item.diaChi}</p>
                        <p><strong>Lý do đến khám:</strong> {item.lyDoDenKham}</p>
                        <p><strong>Ghi chú:</strong> {item.ghiChu || 'Không có'}</p>
                        <p><strong>Ngày tạo:</strong> {dayjs(item.ngayTao).format('HH:mm DD/MM/YYYY')}</p>
                        <p><strong>Ngày khám:</strong> {dayjs(item.ngayDangKyKham).format('HH:mm DD/MM/YYYY')}</p>
                        <p><strong>Trạng thái:</strong> <Tag color={item.trangThai === 1 ? 'green' : 'red'}>{item.trangThai === 1 ? 'Đã đăng ký' : 'Chưa đăng ký'}</Tag></p>
                    </Card>
                </List.Item>
            )}
        />
    );

    return (
        <div style={{ padding: '24px' }}>
            <Title level={4}>Quản lý Đăng ký online</Title>
            <Space style={{ marginBottom: 16, flexWrap: 'wrap' }}>
                <RangePicker 
                    value={dateRange}
                    onChange={(dates) => {
                        if (dates) {
                            setDateRange([dates[0]!, dates[1]!]);
                        }
                    }}
                />
                <Input
                    placeholder="Tìm theo tên bệnh nhân"
                    value={nameSearch}
                    onChange={(e) => setNameSearch(e.target.value)}
                    style={{ width: 200 }}
                    onPressEnter={handleFilter}
                />
                <Button type="primary" icon={<SearchOutlined />} onClick={handleFilter} loading={loading}>
                    Lọc
                </Button>
                {hasPermission('DangKyOnline') && (
                    <Button type="primary" icon={<PlusOutlined />} onClick={showCreateModal}>
                        Tạo mới đăng ký
                    </Button>
                )}
            </Space>
            
            {screens.md ? renderDesktopView() : renderMobileView()}

            {editingRecord && (
                <Modal
                    title="Chỉnh sửa thông tin đăng ký"
                    open={isEditModalVisible}
                    onCancel={handleEditCancel}
                    footer={null}
                    destroyOnClose
                >
                    <Form
                        form={editForm}
                        layout="vertical"
                        onFinish={handleEditSubmit}
                        initialValues={{
                            ...editingRecord,
                            ngayDangKyKham: dayjs(editingRecord.ngayDangKyKham),
                        }}
                    >
                        <Form.Item name="tenBn" label="Họ và Tên" rules={[{ required: true, message: 'Vui lòng nhập họ và tên!' }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item name="namSinh" label="Năm Sinh" rules={[{ required: true, message: 'Vui lòng nhập năm sinh!' }]}>
                            <InputNumber style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name="sdt" label="Số Điện Thoại" rules={[{ required: true, message: 'Vui lòng nhập số điện thoại!' }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item name="diaChi" label="Địa chỉ" rules={[{ required: true, message: 'Vui lòng nhập địa chỉ!' }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item name="ngayDangKyKham" label="Ngày Đăng Ký Khám" rules={[{ required: true, message: 'Vui lòng chọn ngày!' }]}>
                            <DatePicker
                                showTime
                                format="DD/MM/YYYY HH:mm"
                                style={{ width: '100%' }}
                                disabledDate={disabledDate}
                            />
                        </Form.Item>
                        <Form.Item name="lyDoDenKham" label="Lý Do Đến Khám" rules={[{ required: true, message: 'Vui lòng nhập lý do!' }]}>
                            <TextArea rows={4} />
                        </Form.Item>
                        <Form.Item name="ghiChu" label="Ghi Chú">
                            <TextArea rows={2} />
                        </Form.Item>
                        <Form.Item>
                            <Button type="primary" htmlType="submit">
                                Lưu thay đổi
                            </Button>
                        </Form.Item>
                    </Form>
                </Modal>
            )}

            <Modal
                title="Tạo mới đăng ký"
                open={isCreateModalVisible}
                onCancel={handleCreateCancel}
                footer={null}
                destroyOnClose
            >
                <Form
                    form={createForm}
                    layout="vertical"
                    onFinish={handleCreateSubmit}
                >
                    <Form.Item name="tenBn" label="Họ và Tên" rules={[{ required: true, message: 'Vui lòng nhập họ và tên!' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="namSinh" label="Năm Sinh" rules={[
                        { required: true, message: 'Vui lòng nhập năm sinh!' },
                        { type: 'number', transform: value => Number(value), message: 'Năm sinh phải là số' },
                        { validator: (_, value) => 
                            value && value > 1900 && value <= dayjs().year()
                            ? Promise.resolve() 
                            : Promise.reject(new Error('Năm sinh không hợp lệ'))
                        }
                    ]}>
                        <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="sdt" label="Số Điện Thoại" rules={[{ required: true, message: 'Vui lòng nhập số điện thoại!' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="diaChi" label="Địa chỉ" rules={[{ required: true, message: 'Vui lòng nhập địa chỉ!' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="ngayDangKyKham" label="Ngày Đăng Ký Khám" rules={[{ required: true, message: 'Vui lòng chọn ngày!' }]}>
                        <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%' }} disabledDate={disabledDate} />
                    </Form.Item>
                    <Form.Item name="lyDoDenKham" label="Lý Do Đến Khám" rules={[{ required: true, message: 'Vui lòng nhập lý do!' }]}>
                        <TextArea rows={4} />
                    </Form.Item>
                    <Form.Item name="ghiChu" label="Ghi Chú">
                        <TextArea rows={2} />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit">
                            Tạo đăng ký
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default DangKyOnlineManagement;
